import { storage } from '@visiba/std';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { TableColumn } from '../table.component';

interface StoredColumnPreferences {
	order: string[];
	hidden: string[];
}

export interface TableColumnPreferencesItem {
	/**
	 * The column's `key`, matching `TableColumn.key`.
	 */
	key: string;

	/**
	 * Plain-text label for the column - `TableColumn.label`, falling back to
	 * `TableColumn.header` when it's already a string, or `key` as a last
	 * resort.
	 */
	label: string;

	/**
	 * Whether the column is currently visible.
	 */
	visible: boolean;

	/**
	 * Whether the column can be hidden - `TableColumn.canHide`. When `false`,
	 * `toggleVisibility` is a no-op for it, and it's always shown regardless
	 * of `defaultHiddenColumns`.
	 */
	canHide: boolean;
}

export interface TableColumnPreferences<TRow> {
	/**
	 * The visible columns, reordered to match the user's preference - pass
	 * this straight into `<Table columns={...} />`.
	 */
	columns: TableColumn<TRow>[];

	/**
	 * Every column (visible and hidden), in the user's current order - use
	 * this to render a column visibility/reorder menu, e.g. `TableColumnMenu`.
	 */
	items: TableColumnPreferencesItem[];

	/**
	 * Toggles a column between visible and hidden.
	 */
	toggleVisibility: (key: string) => void;

	/**
	 * Moves a column from one position to another. `toIndex` is clamped to
	 * the valid range. Indexes refer to positions within `items`.
	 */
	reorder: (fromIndex: number, toIndex: number) => void;
}

export interface UseTableColumnPreferencesOptions<TRow> {
	/**
	 * Unique key used to persist the user's preference to `localStorage`.
	 * Must be unique per table - e.g. `'patients-table'`.
	 */
	storageKey: string;

	/**
	 * The columns to manage, in their default order.
	 */
	columns: TableColumn<TRow>[];

	/**
	 * Keys of columns that should be hidden by default, before the user has
	 * made any choice of their own. Has no effect once a preference has been
	 * saved (including after the user has toggled anything).
	 */
	defaultHiddenColumns?: string[];
}

const columnPreferencesStorage = storage<Record<string, StoredColumnPreferences>>({
	prefix: 'cellula-table-column-preferences:',
});

function reconcile(columnKeys: string[], order: string[], hidden: string[], nonHidableKeys: Set<string>): StoredColumnPreferences {
	const currentKeys = new Set(columnKeys);
	const reconciledOrder = order.filter((key) => currentKeys.has(key));

	for (const key of columnKeys) {
		if (!reconciledOrder.includes(key)) {
			reconciledOrder.push(key);
		}
	}

	return {
		order: reconciledOrder,
		hidden: hidden.filter((key) => currentKeys.has(key) && !nonHidableKeys.has(key)),
	};
}

function columnLabel<TRow>(column: TableColumn<TRow>): string {
	if (column.label) return column.label;
	if (typeof column.header === 'string') return column.header;
	return column.key;
}

/**
 * Manages column visibility and order for a `Table`, persisted to
 * `localStorage`. Pairs with `TableColumnMenu`, which renders the toggle/
 * reorder UI on top of this hook's state.
 *
 * ```tsx
 * const columnPreferences = useTableColumnPreferences({
 *   storageKey: 'patients-table',
 *   columns,
 *   defaultHiddenColumns: ['id'],
 * });
 *
 * <Table columns={columnPreferences.columns} rows={rows} />
 * <TableColumnMenu preferences={columnPreferences} />
 * ```
 */
export function useTableColumnPreferences<TRow>({
	storageKey,
	columns,
	defaultHiddenColumns = [],
}: UseTableColumnPreferencesOptions<TRow>): TableColumnPreferences<TRow> {
	const columnsByKey = useMemo(() => new Map(columns.map((column) => [column.key, column])), [columns]);
	const columnKeys = useMemo(() => columns.map((column) => column.key), [columns]);
	const columnKeysSignature = columnKeys.join('|');
	const nonHidableKeys = useMemo(
		() => new Set(columns.filter((column) => column.canHide === false).map((column) => column.key)),
		[columns],
	);
	const nonHidableKeysRef = useRef(nonHidableKeys);
	nonHidableKeysRef.current = nonHidableKeys;

	const defaultHiddenColumnsRef = useRef(defaultHiddenColumns);
	defaultHiddenColumnsRef.current = defaultHiddenColumns;

	const [preferences, setPreferences] = useState<StoredColumnPreferences>(() => {
		const saved = columnPreferencesStorage.get(storageKey);
		if (saved) return reconcile(columnKeys, saved.order, saved.hidden, nonHidableKeys);
		return reconcile(columnKeys, columnKeys, defaultHiddenColumns, nonHidableKeys);
	});

	// Keep the reconciled state in sync when the *set* of column keys changes
	// (e.g. a column is added/removed in code). `columnKeysSignature` only
	// changes when the actual key set changes, so this doesn't run on every
	// render even though `columns` is typically a fresh array each time.
	const lastSignatureRef = useRef(columnKeysSignature);
	const lastStorageKeyRef = useRef(storageKey);
	if (lastStorageKeyRef.current !== storageKey) {
		// `storageKey` changed (e.g. the caller switched which table/dataset is
		// shown) - re-read from storage for the *new* key rather than keep
		// reconciling state loaded for the old one, which was only read once
		// in the initial `useState`.
		lastStorageKeyRef.current = storageKey;
		lastSignatureRef.current = columnKeysSignature;
		const saved = columnPreferencesStorage.get(storageKey);
		setPreferences(
			saved
				? reconcile(columnKeys, saved.order, saved.hidden, nonHidableKeys)
				: reconcile(columnKeys, columnKeys, defaultHiddenColumnsRef.current, nonHidableKeys),
		);
	} else if (lastSignatureRef.current !== columnKeysSignature) {
		lastSignatureRef.current = columnKeysSignature;
		setPreferences((prev) => reconcile(columnKeys, prev.order, prev.hidden, nonHidableKeys));
	}

	const preferencesRef = useRef(preferences);
	preferencesRef.current = preferences;

	const persist = useCallback(
		(updater: (prev: StoredColumnPreferences) => StoredColumnPreferences) => {
			const next = updater(preferencesRef.current);
			preferencesRef.current = next;
			columnPreferencesStorage.set(storageKey, next);
			setPreferences(next);
		},
		[storageKey],
	);

	const toggleVisibility = useCallback(
		(key: string) => {
			if (nonHidableKeysRef.current.has(key)) return;
			persist((prev) => ({
				...prev,
				hidden: prev.hidden.includes(key) ? prev.hidden.filter((hiddenKey) => hiddenKey !== key) : [...prev.hidden, key],
			}));
		},
		[persist],
	);

	const reorder = useCallback(
		(fromIndex: number, toIndex: number) => {
			persist((prev) => {
				const clampedToIndex = Math.max(0, Math.min(toIndex, prev.order.length - 1));
				if (fromIndex === clampedToIndex || fromIndex < 0 || fromIndex >= prev.order.length) return prev;

				const nextOrder = [...prev.order];
				const [moved] = nextOrder.splice(fromIndex, 1);
				nextOrder.splice(clampedToIndex, 0, moved);

				return { ...prev, order: nextOrder };
			});
		},
		[persist],
	);

	const items = useMemo<TableColumnPreferencesItem[]>(
		() =>
			preferences.order.map((key) => {
				const column = columnsByKey.get(key);
				return {
					key,
					label: column ? columnLabel(column) : key,
					visible: !preferences.hidden.includes(key),
					canHide: !nonHidableKeys.has(key),
				};
			}),
		[preferences, columnsByKey, nonHidableKeys],
	);

	const visibleColumns = useMemo<TableColumn<TRow>[]>(
		() =>
			preferences.order
				.filter((key) => !preferences.hidden.includes(key))
				.map((key) => columnsByKey.get(key))
				.filter((column): column is TableColumn<TRow> => column != null),
		[preferences, columnsByKey],
	);

	return { columns: visibleColumns, items, toggleVisibility, reorder };
}
